import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { migrateLegacyConnectTokenOnce } from './migrateLegacyConnectToken';

// Adopt any pre-split `connect-token` blob before this store hydrates.
migrateLegacyConnectTokenOnce();

// Which auth flow established the current session, by source:
// - 'portal'  — AcombaGO inside the host iframe: AcceoConnect held server-side, recovers via re-nonce.
//               The browser never sees the AcceoConnect token (the backend sources it from the Connect
//               session), so Connect-token headers are NOT sent from here.
// - 'browser' — AcombaGO opened directly in a browser: the ONLY session kind where AcceoConnect lives
//               client-side — the token is sent on each request (X-Connect-Token) and refreshed against
//               the live OIDC token, recovering via OIDC silent renew. Has a tenant picker.
// - 'desktop' — launched from the Acomba desktop app (/desktop?src=…): no AcceoConnect at all, single
//               workspace resolved by ICIdentifiantPublique, no picker.
// All three use our app token as the API bearer, so the bearer alone doesn't distinguish them — this does.
export type SessionKind = 'portal' | 'browser' | 'desktop' | null;

// Session-lifecycle metadata that is neither a credential nor the app token itself: how the session
// was established (sessionKind) and the state of the portal cold-session recovery (reconnecting /
// connectSessionFailed, read by ReconnectingOverlay).
interface SessionState {
    sessionKind: SessionKind;
    // True while a portal re-nonce is in flight (cold session recovery). Drives the reconnecting
    // overlay so recovery is never a silent bounce.
    reconnecting: boolean;
    // Set when the host fails to re-mint a portal nonce — the AcceoConnect session cannot be
    // recovered. Components read this to show an actionable "reopen from AcombaGO" message.
    connectSessionFailed: boolean;
    setSessionKind: (kind: SessionKind) => void;
    setReconnecting: (reconnecting: boolean) => void;
    setConnectSessionFailed: (failed: boolean) => void;
    clearSessionMeta: () => void;
}

/**
 * Persist migration for `session-meta`.
 *
 * v1 reassigns the meaning of 'desktop': before v1 it meant "AcombaGO opened directly in a browser"
 * (OIDC + client-side AcceoConnect) — that case is now 'browser'; 'desktop' now means the Acomba
 * desktop-app flow (no AcceoConnect). A session persisted under v0 with sessionKind 'desktop' is
 * therefore the old browser meaning and must be rewritten, or the user would be silently mistreated
 * as a desktop-app session (wrong refresh endpoint, dropped X-Connect-Token / X-Sso-Bearer headers,
 * hidden change-company/logout).
 *
 * v2 drops the recovery flags from storage entirely (see the store's `partialize`). Blobs written
 * under v1 can carry `connectSessionFailed: true`, which would hydrate the blocking overlay back
 * over a healthy app, so they are stripped here rather than merged.
 *
 * Exported for testing.
 */
export function migrateSessionMeta(persisted: unknown, version: number): Partial<SessionState> {
    const state = { ...((persisted ?? {}) as Partial<SessionState>) };
    if (version < 1 && state.sessionKind === 'desktop') {
        state.sessionKind = 'browser';
    }
    if (version < 2) {
        delete state.reconnecting;
        delete state.connectSessionFailed;
    }
    return state;
}

export const useSessionStore = create<SessionState>()(
    persist(
        (set) => ({
            sessionKind: null,
            reconnecting: false,
            connectSessionFailed: false,
            setSessionKind: (sessionKind: SessionKind) => set({ sessionKind }),
            setReconnecting: (reconnecting: boolean) => set({ reconnecting }),
            setConnectSessionFailed: (connectSessionFailed: boolean) => set({ connectSessionFailed }),
            clearSessionMeta: () => set({
                sessionKind: null, reconnecting: false, connectSessionFailed: false,
            }),
        }),
        {
            name: 'session-meta',
            version: 2,
            migrate: migrateSessionMeta,
            // Only sessionKind survives a reload. The recovery flags describe an in-flight attempt,
            // not the session: persisting them meant a re-nonce interrupted by a force-reload (or a
            // host that answered `nonce-refresh-error` once) left a full-screen blocking overlay
            // pinned across every later page load, with no way out but clearing storage by hand.
            partialize: (state) => ({ sessionKind: state.sessionKind }),
        }
    )
);
